import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import Modal from "../../../../ui/Modal/Modal";
import { useCallback, useEffect, useState } from "react";
import GenerateModal, {
  GenerateArticleModalFormData,
} from "../../../../components/GenerateModal/GenerateModal";
import { ArticleStatus } from "../../../../__generated__/gql/graphql";
import { useToast } from "../../../../ui/Toast/ToastContext";
import InsufficientCreditsModal from "../../../../components/InsufficientCreditsModal/InsufficientCreditsModal";
import useUser from "../../../../providers/UserProvider/useUser";

const GeneratePatternModalQuery = graphql(/* GraphQL */ `
  query generatePatternModal(
    $id: String!
    $regenerateBrief: Boolean
    $regenerateOutline: Boolean
    $regenerateContent: Boolean
    $regenerateLinks: Boolean
  ) {
    articleCounts(input: { filters: [{ patternIds: [$id] }] }) {
      total
      processing
      generated
      published
      draft
    }
    creditsRequired(
      input: {
        filters: [{ patternIds: [$id] }]
        regenerateBrief: $regenerateBrief
        regenerateOutline: $regenerateOutline
        regenerateContent: $regenerateContent
        regenerateLinks: $regenerateLinks
      }
    )
  }
`);

const GeneratePatternArticlesMutation = graphql(/* GraphQL */ `
  mutation generatePatternArticles($input: GenerateArticlesInput!) {
    generateArticles(input: $input) {
      ... on GenerateArticlesResult {
        count
      }
      ... on InsufficientCreditsError {
        __typename
        message
      }
    }
  }
`);

function GeneratePatternModal({
  isOpen,
  onClose,
  id,
}: {
  isOpen: boolean;
  onClose: () => void;
  id: string;
}) {
  const { articlesRemaining } = useUser();
  const [articlesRemainingOnOpen, setArticlesRemainingOnOpen] =
    useState(articlesRemaining);
  const [variables, setVariables] = useState({
    regenerateBrief: false,
    regenerateOutline: false,
    regenerateContent: true,
    regenerateLinks: true,
  });
  const { showToast } = useToast();
  const [generatePatternModalResponse] = useQuery({
    query: GeneratePatternModalQuery,
    pause: !isOpen,
    variables: {
      id,
      ...variables,
    },
  });
  const [{ fetching }, generatePatternArticles] = useMutation(
    GeneratePatternArticlesMutation,
  );

  useEffect(() => {
    if (isOpen) {
      setArticlesRemainingOnOpen(articlesRemaining);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const articleCounts = generatePatternModalResponse.data?.articleCounts;
  const creditsRequired = generatePatternModalResponse.data?.creditsRequired;

  const totalArticlesToGenerate = articleCounts
    ? articleCounts.total - articleCounts.processing
    : 0;

  const onChange = useCallback(
    (values: {
      regenerateBrief?: boolean;
      regenerateOutline?: boolean;
      regenerateContent?: boolean;
      regenerateLinks?: boolean;
    }) =>
      setVariables((previous) => ({
        ...previous,
        ...values,
      })),
    [],
  );

  const onSubmit = useCallback(
    async (data: GenerateArticleModalFormData) => {
      const generatePatternArticlesResponse = await generatePatternArticles({
        input: {
          regenerateBrief: data.regenerateBrief,
          regenerateOutline: data.regenerateOutline,
          regenerateContent: data.regenerateContent,
          regenerateLinks: data.regenerateLinks,
          filters: [
            {
              patternIds: [id],
              status: !data.regenerate
                ? [{ notIncludes: [ArticleStatus.Generated] }]
                : undefined,
            },
          ],
        },
      });

      if (
        generatePatternArticlesResponse.data?.generateArticles.__typename ===
        "InsufficientCreditsError"
      ) {
        showToast({
          variant: "error",
          title: "You've run out of article credits",
        });
      } else if (articleCounts) {
        const articles = data.regenerate
          ? totalArticlesToGenerate
          : totalArticlesToGenerate - articleCounts.generated;
        showToast({
          title: `${articles} ${
            articles === 1 ? "article" : "articles"
          } queued for generation`,
        });
      }
      onClose();
    },
    [
      articleCounts,
      generatePatternArticles,
      id,
      onClose,
      showToast,
      totalArticlesToGenerate,
    ],
  );

  if (creditsRequired && creditsRequired > articlesRemainingOnOpen) {
    return (
      <InsufficientCreditsModal
        isOpen={isOpen}
        onClose={onClose}
        articleCount={articleCounts?.total ?? 0}
      />
    );
  }

  let title = `Confirm ${
    articleCounts?.generated ? "regeneration" : "generation"
  } of ${totalArticlesToGenerate} ${
    totalArticlesToGenerate === 1 ? "article" : "articles"
  }`;

  if (totalArticlesToGenerate === 0) {
    title = "No articles to generate";
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={
        articleCounts && articleCounts.processing > 0
          ? "Articles that are currently processing have been excluded from your selection."
          : undefined
      }
      loading={
        generatePatternModalResponse.fetching ||
        !generatePatternModalResponse.data
      }
    >
      <GenerateModal
        onClose={onClose}
        onSubmit={onSubmit}
        onChange={onChange}
        fetching={fetching || !isOpen}
        articleCounts={articleCounts}
        creditsRequired={creditsRequired ?? 0}
      />
    </Modal>
  );
}

export default GeneratePatternModal;
